import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Check, X, Loader2, Eraser } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { Confirm } from "./Confirm";
import { ReviewAutomation } from "./ReviewAutomation";
import { http, apiError, formatDate, DIVISIONS } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const EMPTY = { display_name: "", company: "", email: "", rating: null, division: "", body: "", review_date: new Date().toISOString().slice(0, 10), status: "approved", published: false, featured: false, permission: "unknown" };
const PERM = { granted: "Permission granted", not_granted: "No permission", unknown: "Permission unknown" };
const STRIP = ["id", "created_at", "source", "email_status", "is_sample"];
const divName = (k) => DIVISIONS.find((d) => d.key === k)?.name || "No division";

const Editor = ({ review, onClose, onSaved }) => {
  const [r, setR] = useState({ ...EMPTY, ...review });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setR({ ...r, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const canPublish = r.status === "approved" && r.permission === "granted";
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...r, rating: r.rating ? Number(r.rating) : null };
      STRIP.forEach((k) => delete body[k]);
      if (review?.id) await http.put(`/admin/reviews/${review.id}`, body);
      else await http.post("/admin/reviews", body);
      toast.success("Review saved");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="review-editor">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Displayed name *</label><input required className="field" value={r.display_name} onChange={set("display_name")} data-testid="admin-review-name-input" /></div>
        <div><label className="label">Company</label><input className="field" value={r.company} onChange={set("company")} /></div>
        <div><label className="label">Review date</label><input type="date" className="field" value={r.review_date} onChange={set("review_date")} data-testid="admin-review-date-input" /></div>
        <div>
          <label className="label">Division</label>
          <select className="field" value={r.division || ""} onChange={set("division")} data-testid="admin-review-division-select">
            <option value="">No division</option>
            {DIVISIONS.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Rating (only if provided by customer)</label>
          <select className="field" value={r.rating || ""} onChange={(e) => setR({ ...r, rating: e.target.value ? Number(e.target.value) : null })} data-testid="admin-review-rating-select">
            <option value="">No rating given</option>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5</option>)}
          </select>
        </div>
      </div>
      <div><label className="label">Review text *</label><textarea required rows={5} className="field" value={r.body} onChange={set("body")} data-testid="admin-review-body-input" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Moderation</label>
          <select className="field" value={r.status} onChange={set("status")} data-testid="admin-review-status-select">
            <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label className="label">Publication permission</label>
          <select className="field" value={r.permission} onChange={set("permission")} data-testid="admin-review-permission-select">
            {Object.entries(PERM).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap gap-6 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" disabled={!canPublish} checked={r.published && canPublish} onChange={set("published")} className="accent-[#d4af37] w-4 h-4" data-testid="admin-review-published-checkbox" /> Published</label>
        <label className="flex items-center gap-2"><input type="checkbox" disabled={!canPublish || !r.published} checked={r.featured && r.published && canPublish} onChange={set("featured")} className="accent-[#d4af37] w-4 h-4" data-testid="admin-review-featured-checkbox" /> Feature on homepage</label>
      </div>
      {!canPublish && <p className="text-xs text-amber-300">A review can only be published when it is approved and the customer has granted permission.</p>}
      <div className="flex gap-3 pt-2">
        <button className="btn-gold" disabled={busy} data-testid="admin-review-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save review</button>
        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

export default function ReviewsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-reviews"], queryFn: async () => (await http.get("/admin/reviews")).data });
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const all = data?.items || [];
  const samples = all.filter((r) => r.is_sample).length;
  const items = all.filter((r) => filter === "all" || (filter === "sample" ? r.is_sample : filter === "genuine" ? !r.is_sample : r.division === filter));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-reviews"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
    qc.invalidateQueries({ queryKey: ["home"] });
    qc.invalidateQueries({ queryKey: ["reviews"] });
  };
  const quick = async (r, patch, msg) => {
    try {
      const body = { ...r, ...patch };
      STRIP.forEach((k) => delete body[k]);
      await http.put(`/admin/reviews/${r.id}`, body);
      toast.success(msg);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeSamples = async () => {
    try {
      const { data: res } = await http.delete("/admin/reviews/samples");
      toast.success(`Removed ${res.deleted} sample review${res.deleted === 1 ? "" : "s"}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-reviews">
      <AdminTitle title="Client reviews" sub="Only approved reviews with publication permission can appear on the website.">
        {samples > 0 && (
          <Confirm title={`Remove all ${samples} sample reviews?`} text="This permanently deletes only the reviews marked as Sample Review. Genuine customer reviews are not affected." onConfirm={removeSamples}>
            <button className="btn-ghost hover:!border-rose-400" data-testid="remove-sample-reviews-button"><Eraser className="w-4 h-4" /> Remove All Sample Reviews</button>
          </Confirm>
        )}
        <button className="btn-gold" onClick={() => setEditing({})} data-testid="add-review-button"><Plus className="w-4 h-4" /> Add Review</button>
      </AdminTitle>
      <ReviewAutomation />
      {all.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-review-filter">
          {[["all", "All"], ...DIVISIONS.map((d) => [d.key, d.short]), ["sample", "Sample"], ["genuine", "Genuine"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs font-mono uppercase border transition-colors ${filter === k ? "border-[var(--gold)] text-gold" : "border-white/15 text-slate-300 hover:border-white/40"}`} data-testid={`admin-review-filter-${k}`}>{l}</button>
          ))}
        </div>
      )}
      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <p className="border border-dashed border-white/20 p-12 text-center text-slate-300">{all.length ? "No reviews match this filter." : "No reviews yet. Add a review a client has shared with you, or wait for website submissions."}</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="border hairline bg-[var(--ink-2)] p-5 flex flex-col lg:flex-row gap-5" data-testid={`admin-review-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 text-[0.65rem] font-mono uppercase">
                  <span className={`px-2 py-1 border ${r.status === "approved" ? "border-emerald-400/40 text-emerald-300" : r.status === "rejected" ? "border-rose-400/40 text-rose-300" : "border-[var(--gold)] text-gold"}`}>{r.status}</span>
                  <span className="px-2 py-1 border border-white/15 text-slate-300">{PERM[r.permission]}</span>
                  <span className={`px-2 py-1 border ${r.published ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-slate-400"}`}>{r.published ? "Published" : "Not published"}</span>
                  {r.featured && <span className="px-2 py-1 border border-[var(--gold)] text-gold">Featured</span>}
                  <span className="px-2 py-1 border border-white/15 text-slate-300" data-testid={`admin-review-division-${r.id}`}>{divName(r.division)}</span>
                  {r.is_sample && <span className="px-2 py-1 border border-amber-400/50 text-amber-300" data-testid={`admin-review-sample-${r.id}`}>Sample review</span>}
                  <span className="px-2 py-1 text-slate-400">via {r.source === "website" ? "website form" : r.source === "sample" ? "demo content" : r.source === "review_request" ? `review request${r.inquiry_ref ? ` · ${r.inquiry_ref}` : ""}` : "owner"}</span>
                </div>
                <p className="mt-3 text-slate-100 font-medium">{r.display_name}{r.company ? ` · ${r.company}` : ""} <span className="text-slate-400 text-sm font-normal">· {formatDate(r.review_date)}{r.rating ? ` · ${r.rating}/5` : ""}</span></p>
                <p className="text-sm text-slate-300 mt-2 line-clamp-3">{r.body}</p>
                {r.email && <p className="text-xs text-slate-400 mt-2 break-all">Private: {r.email}</p>}
              </div>
              <div className="flex flex-wrap items-start gap-2">
                {r.status !== "approved" && <button onClick={() => quick(r, { status: "approved" }, "Approved")} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`review-approve-${r.id}`}><Check className="w-3 h-3" /> Approve</button>}
                {r.status !== "rejected" && <button onClick={() => quick(r, { status: "rejected", published: false, featured: false }, "Rejected")} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`review-reject-${r.id}`}><X className="w-3 h-3" /> Reject</button>}
                {r.status === "approved" && r.permission === "granted" && (
                  <button onClick={() => quick(r, { published: !r.published, featured: r.published ? false : r.featured }, r.published ? "Unpublished" : "Published")} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`review-publish-${r.id}`}>{r.published ? "Unpublish" : "Publish"}</button>
                )}
                {r.published && <button onClick={() => quick(r, { featured: !r.featured }, r.featured ? "Unfeatured" : "Featured")} className={`btn-ghost !py-2 !px-3 !text-xs ${r.featured ? "!border-[var(--gold)] !text-gold" : ""}`} data-testid={`review-feature-${r.id}`}><Star className="w-3 h-3" /> {r.featured ? "Featured" : "Feature"}</button>}
                <button onClick={() => setEditing(r)} className="btn-ghost !py-2 !px-3 !text-xs" data-testid={`review-edit-${r.id}`}><Pencil className="w-3 h-3" /> Edit</button>
                <Confirm title="Delete this review?" text="This cannot be undone." onConfirm={() => http.delete(`/admin/reviews/${r.id}`).then(refresh)}>
                  <button className="btn-ghost !py-2 !px-3 !text-xs hover:!border-rose-400" data-testid={`review-delete-${r.id}`}><Trash2 className="w-3 h-3" /></button>
                </Confirm>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl bg-[var(--ink-2)] border-white/10 rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium">{editing?.id ? "Edit review" : "Add review"}</DialogTitle>
            <DialogDescription className="text-slate-300">Enter only genuine feedback a client has provided.</DialogDescription>
          </DialogHeader>
          {editing && <Editor review={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
